import { FC, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import cls from 'clsx';
import { CurrencyCurrency } from '@linktivity/ars-neo';
import { Delete as DeleteIcon } from '@linktivity/link-icons';
import { Button } from '@linktivity/link-ui';
import { Confirm, Img, Price } from '@link/share/components';
import { resolveParticipationDate } from '@link/share/utils';
import { CartItem as HeaderCartItem } from './header.types';
import stylesCart from './cart.module.css';

interface CartItemProps {
  item: HeaderCartItem;
  className?: string;
  disabled?: boolean;
  onRemove?: (id: string) => void;
  currentCurrency?: CurrencyCurrency;
}

const CartItem: FC<CartItemProps> = ({
  item,
  className,
  disabled,
  onRemove,
  currentCurrency
}) => {
  const { t } = useTranslation();
  const [isDeleteConfirmVisible, setIsDeleteConfirmVisible] = useState(false);
  const activityForm = item.session.form?.activityForm;
  // A cart row carries a session and a product id, never the plan, and the
  // drawer is not worth a product fetch per row to learn one — so a session
  // written before `participationDate` existed shows no date here until it
  // reaches the confirm page, which has the plan and reads it correctly.
  const participationDate = resolveParticipationDate(activityForm);
  const itemTotalAmount = (activityForm?.planUnitItems ?? []).reduce(
    (acc, unit) => acc + (unit.amount ?? 0) * (unit.count ?? 0),
    0
  );
  const handleDeleteClick = useCallback(() => {
    setIsDeleteConfirmVisible(true);
  }, []);
  const handleDeleteConfirm = useCallback(() => {
    setIsDeleteConfirmVisible(false);
    onRemove?.(item.id);
  }, [item.id, onRemove]);

  return (
    <div className={cls(stylesCart.cartItemWrapper, className)}>
      <div className={stylesCart.cartItemContent}>
        <div className={stylesCart.cartItemImage}>
          <Img
            className={stylesCart.productImage}
            src={activityForm?.thumbnailUrl}
            alt={activityForm?.activityTitle}
          />
        </div>
        <div className={stylesCart.info}>
          <div className={stylesCart.infoTitle}>
            {activityForm?.activityTitle}
          </div>
          <div className={stylesCart.infoPlan}>{activityForm?.planTitle}</div>
          <div className={stylesCart.infoUnit}>
            {(activityForm?.planUnitItems ?? []).map(unit => (
              <div key={unit.unitId}>
                {unit.title}
                {t('share.common.multiply')}
                {unit.count}
              </div>
            ))}
          </div>
          {participationDate && (
            <div className={stylesCart.infoDate}>
              {t('share.common.withParentheses', {
                value1: participationDate,
                value2: activityForm?.planStartTime
              })}
            </div>
          )}
        </div>
      </div>
      <div className={stylesCart.control}>
        {onRemove ? (
          <>
            <Button
              className={stylesCart.deleteButton}
              variant="text-danger"
              size="small"
              onClick={handleDeleteClick}
            >
              <DeleteIcon className={stylesCart.deleteIcon} />
              {t('share.common.delete')}
            </Button>
            <Confirm
              visible={isDeleteConfirmVisible}
              danger
              title={t('share.common.delete')}
              content={t('share.cart.deleteConfirm', {
                activityName: activityForm?.activityTitle
              })}
              confirmText={t('share.common.delete')}
              onClose={() => setIsDeleteConfirmVisible(false)}
              onConfirm={handleDeleteConfirm}
            />
          </>
        ) : (
          <div className={stylesCart.option}>{t('share.payment.option')}</div>
        )}
        {disabled ? (
          <div className={stylesCart.cartItemPriceDisabled}>
            {t('share.cart.unavailable')}
          </div>
        ) : (
          <div className={stylesCart.cartItemPrice}>
            <Price amount={itemTotalAmount} currencyCode={currentCurrency} />
          </div>
        )}
      </div>
    </div>
  );
};

export default CartItem;
